// AGC: FarmProHub Documentation Page
// AGC: Guides, FAQ, and documentation

import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { FileText, ChevronDown, ChevronRight, ExternalLink, Book, HelpCircle, Shield, Coins, TrendingUp, GitCommit, Calendar, Target } from 'lucide-react'
import { parseChangelog, sortEntriesByDate, ChangelogEntry } from '../utils/parseChangelog'

interface DocSection {
  id: string
  title: string
  icon: React.ReactNode
  content: string[]
}

interface FAQItem {
  question: string
  answer: string
}

interface DocsPageProps {
  initialSection?: string
  initialExpandedEntry?: number | null
}

export const DocsPage: React.FC<DocsPageProps> = ({ 
  initialSection = 'vision',
  initialExpandedEntry = null 
}) => {
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null)
  const [activeSection, setActiveSection] = useState<string>(initialSection)
  const [changelogEntries, setChangelogEntries] = useState<ChangelogEntry[]>([])
  const [expandedEntry, setExpandedEntry] = useState<number | null>(initialExpandedEntry)
  const [loadingChangelog, setLoadingChangelog] = useState(true)

  const docSections: DocSection[] = [
    {
      id: 'vision',
      title: 'FarmProHub Vision',
      icon: <Target className="w-5 h-5 text-green-400" />,
      content: [
        'FarmProHub is a long-term ecosystem designed to support the creation, governance, and growth of on-chain products built on Base.',
        'It represents a shift from managing large teams and complex operational structures to building directly and rapidly with the support of modern AI tools.',
        'FarmProHub is a governance and revenue-sharing ecosystem that supports every product created on Base, providing a unified community instead of new companies for each idea.',
        'The community shares 50% of all revenues through shared incentives, and participates in community-driven development through governance voting.',
        'After 25 years of entrepreneurship, FarmProHub is intentionally lightweight — an ecosystem, not a corporation.',
        'It provides freedom to create without bureaucracy, while giving the community the ability to adopt, promote, and expand products organically.',
        'Base offers the leadership, clarity, culture, and technical direction aligned with long-term goals — real use cases, not speculation.',
        'Origami Battles is the first product, demonstrating fully on-chain game logic, transparent mechanics, NFT-based utility, and strategy depth.',
        'FarmProHub is not financially motivated — it is built for creation, long-term alignment, transparent innovation, and sustainable growth.',
        'The goal is to create meaningful products at a sustainable pace, enabled by AI, supported by a community that finds value in using and growing them.',
        'The name "FarmPro" comes from the Reddit username used in 2016 when discussing Ethereum and farming applications.',
      ],
    },
    {
      id: 'overview',
      title: 'Overview',
      icon: <Book className="w-5 h-5" />,
      content: [
        'FarmProHub is a decentralized platform where stakers share 50% of all revenue generated by FarmProHub projects.',
        'By staking FarmPro (FP) tokens, you become a stakeholder and earn a proportional share of project revenues.',
        'Stakers also gain voting rights to participate in governance decisions through proposals.',
      ],
    },
    {
      id: 'staking',
      title: 'Staking Guide',
      icon: <Coins className="w-5 h-5" />,
      content: [
        'To stake, connect your wallet and navigate to the Staking page.',
        'Enter the amount of FP tokens you want to stake and confirm the transaction.',
        'Your stake will be locked for a 3-day cooldown period before you can unstake.',
        'You can claim your revenue share at any time without unstaking.',
        'Your voting power equals your staked amount.',
      ],
    },
    {
      id: 'governance',
      title: 'Governance',
      icon: <Shield className="w-5 h-5" />,
      content: [
        'Only the contract owner can create new proposals.',
        'All stakers can vote on active proposals.',
        'Voting power is proportional to your staked amount.',
        'Proposals require 51% approval to pass.',
        'Voting period lasts 7 days from proposal creation.',
        'Passed proposals are executed by the contract owner.',
      ],
    },
    {
      id: 'farmproscore',
      title: 'FarmProScore',
      icon: <TrendingUp className="w-5 h-5" />,
      content: [
        'FarmProScore is a metric that represents your commitment and engagement in the FarmProHub ecosystem.',
        'Your FarmProScore is calculated based on your staked amount in the StakingManager contract.',
        'The score is calculated as: FarmProScore = (Your Staked Amount / 10,000 OGB) * 100%, capped at 100%.',
        'For example: If you stake 5,000 OGB, your FarmProScore = (5,000 / 10,000) * 100% = 50%.',
        'If you stake 10,000 OGB or more, your FarmProScore reaches the maximum of 100%.',
        'A higher FarmProScore indicates a larger stake and greater commitment to the platform.',
        'FarmProScore is displayed on your Home page (top right) and Staking page, showing your current score as a percentage.',
        'Players (non-stakers) have a FarmProScore of 0%. Stakers have scores proportional to their stake (0-100%).',
        'FarmProScore is used across FarmProHub projects (like Origami Battles) to determine benefits and rewards.',
      ],
    },
    {
      id: 'changelog',
      title: 'Changelog',
      icon: <GitCommit className="w-5 h-5" />,
      content: [],
    },
  ]

  // AGC: Update expanded entry when prop changes
  useEffect(() => {
    if (initialExpandedEntry !== null && initialExpandedEntry !== undefined) {
      setExpandedEntry(initialExpandedEntry)
    }
  }, [initialExpandedEntry])

  // AGC: Update active section when prop changes
  useEffect(() => {
    if (initialSection) {
      setActiveSection(initialSection)
    }
  }, [initialSection])

  // AGC: Load changelog entries on mount
  useEffect(() => {
    const loadChangelogs = async () => {
      try {
        setLoadingChangelog(true)
        
        // AGC: Fetch both changelogs from public folder
        const [hubResponse, origamiResponse] = await Promise.all([
          fetch('/CHANGELOG_hub.md').catch(() => null),
          fetch('/changelog_origami.md').catch(() => null),
        ])
        
        const entries: ChangelogEntry[] = []
        
        if (hubResponse?.ok) {
          const hubText = await hubResponse.text()
          const hubEntries = parseChangelog(hubText, 'hub')
          console.log('AGC: Parsed hub changelog:', hubEntries.length, 'entries')
          entries.push(...hubEntries)
        } else {
          console.warn('AGC: Failed to load CHANGELOG_hub.md:', hubResponse?.status, hubResponse?.statusText)
        }
        
        if (origamiResponse?.ok) {
          const origamiText = await origamiResponse.text()
          const origamiEntries = parseChangelog(origamiText, 'origami')
          console.log('AGC: Parsed origami changelog:', origamiEntries.length, 'entries')
          entries.push(...origamiEntries)
        } else {
          console.warn('AGC: Failed to load changelog_origami.md:', origamiResponse?.status, origamiResponse?.statusText)
        }
        
        // AGC: Sort by date (newest first)
        const sortedEntries = sortEntriesByDate(entries)
        setChangelogEntries(sortedEntries)
        
        // AGC: Debug logging
        console.log('AGC: Loaded changelog entries:', {
          hub: hubResponse?.ok ? 'loaded' : 'failed',
          origami: origamiResponse?.ok ? 'loaded' : 'failed',
          totalEntries: sortedEntries.length,
          hubEntries: sortedEntries.filter(e => e.source === 'hub').length,
          origamiEntries: sortedEntries.filter(e => e.source === 'origami').length,
        })
      } catch (error) {
        console.error('AGC: Error loading changelogs:', error)
      } finally {
        setLoadingChangelog(false)
      }
    }
    
    loadChangelogs()
  }, [])

  // AGC: Helper function to correct dates for specific versions
  const getCorrectedDate = (entry: ChangelogEntry): string => {
    // AGC: Correct date for version 6.10.7 (should be 2025-12-05, not 2025-12-02)
    if (entry.version === '6.10.7' && entry.source === 'origami') {
      return '2025-12-05'
    }
    return entry.date
  }

  const faqs: FAQItem[] = [
    {
      question: 'How do I stake FP tokens?',
      answer: 'Connect your wallet, go to the Staking page, enter the amount you want to stake, and confirm the transaction. Your tokens will be locked in the staking contract.',
    },
    {
      question: 'What is the lock period for staking?',
      answer: 'When you request to unstake, there is a 3-day cooldown period before you can withdraw your tokens. This helps maintain stability in the staking pool.',
    },
    {
      question: 'How is revenue distributed?',
      answer: '50% of all revenue from FarmProHub projects goes to stakers. Your share is calculated based on your stake relative to the total staked amount.',
    },
    {
      question: 'How often can I claim rewards?',
      answer: 'You can claim your accumulated rewards at any time. However, revenue redistribution happens every Sunday at 23:30 UTC, so new rewards become available weekly after the distribution.',
    },
    {
      question: 'How do proposals work?',
      answer: 'The contract owner creates proposals for governance decisions. Stakers vote with their staked tokens. A 51% majority is needed to pass a proposal.',
    },
    {
      question: 'Which projects share revenue?',
      answer: 'Currently, Origami Battles shares revenue with stakers. More projects will be added over time, each contributing to the shared revenue pool.',
    },
  ]

  return (
    <main className="pt-[72px] pb-[88px] px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* AGC: Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-4"
        >
          <h1 className="text-2xl font-bold text-white mb-2 flex items-center justify-center gap-2">
            <FileText className="w-7 h-7 text-purple-400" />
            Documentation
          </h1>
          <p className="text-slate-400 text-sm">
            Learn how FarmProHub works
          </p>
        </motion.div>

        {/* AGC: Accordion Category Menu */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden"
        >
          {docSections.map((section) => (
            <div key={section.id} className="border-b border-slate-700/50 last:border-b-0">
              <button
                onClick={() => setActiveSection(activeSection === section.id ? '' : section.id)}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-700/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={section.id === 'vision' && activeSection === section.id ? 'text-green-400' : ''}>
                    {section.icon}
                  </div>
                  <span className={`text-sm font-medium ${
                    activeSection === section.id 
                      ? (section.id === 'vision' ? 'text-green-400 font-bold' : 'text-purple-400')
                      : (section.id === 'vision' ? 'text-green-300' : 'text-slate-300')
                  }`}>
                    {section.title}
                  </span>
                </div>
                {activeSection === section.id ? (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                )}
              </button>
            </div>
          ))}
        </motion.div>

        {/* AGC: Selected Section Content */}
        {activeSection && activeSection !== '' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-4"
          >
            {activeSection === 'changelog' ? (
              // AGC: Changelog Feed (GitHub commits style)
              <div>
                <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                  <GitCommit className="w-6 h-6 text-[#0072CE]" />
                  Changelog
                </h2>
                <h3 className="text-lg font-semibold text-purple-400 mb-4">
                  What I am working on:
                </h3>
                <p className="text-slate-400 text-sm mb-6">
                  Complete development history from FarmProHub and Origami Battles. Entries are sorted chronologically (newest first).
                </p>
                
                {loadingChangelog ? (
                  <div className="text-center py-8">
                    <p className="text-slate-400">Loading changelog entries...</p>
                  </div>
                ) : changelogEntries.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-slate-400">No changelog entries found.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {changelogEntries.map((entry, index) => (
                      <motion.div
                        key={`${entry.source}-${entry.version}-${index}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-slate-800/50 rounded-lg border border-slate-700/50 overflow-hidden"
                      >
                        {/* AGC: Commit Header */}
                        <button
                          onClick={() => setExpandedEntry(expandedEntry === index ? null : index)}
                          className="w-full px-4 py-3 border-b border-slate-700/50 bg-slate-900/30 hover:bg-slate-900/50 transition-colors"
                        >
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full ${
                                entry.source === 'hub' ? 'bg-[#0072CE]' : 'bg-purple-500'
                              }`} />
                  <span className={`text-xs font-semibold ${
                    entry.source === 'hub' ? 'text-[#0072CE]' : 'text-purple-400'
                  }`}>
                    {entry.source === 'hub' ? 'Hub' : 'Origami'}
                  </span>
                              <span className="text-sm font-semibold text-white">
                                v{entry.version}
                              </span>
                              <span className="text-sm text-slate-300">
                                {entry.title}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-2 text-xs text-slate-500">
                                <Calendar className="w-3 h-3" />
                                <span>{getCorrectedDate(entry)}</span>
                              </div>
                              {expandedEntry === index ? (
                                <ChevronDown className="w-4 h-4 text-slate-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              )}
                            </div>
                          </div>
                        </button>

                        {/* AGC: Commit Body (Expandable) */}
                        {expandedEntry === index && (
                          <div className="px-4 py-4 border-t border-slate-700/50">
                            <pre className="text-xs text-slate-300 whitespace-pre-wrap font-mono overflow-x-auto">
                              {entry.fullText}
                            </pre>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* AGC: Footer Note */}
                <div className="mt-6 pt-4 border-t border-slate-700/50 text-center">
                  <p className="text-xs text-slate-500">
                    Changelog combines entries from FarmProHub and Origami Battles. PlayerAI and TournamentAI entries are excluded.
                  </p>
                </div>
              </div>
            ) : (
              // AGC: Regular Documentation Sections
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
                {docSections
                  .filter((s) => s.id === activeSection)
                  .map((section) => (
                    <div key={section.id}>
                      <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${
                        section.id === 'vision' ? 'text-green-400' : 'text-white'
                      }`}>
                        <div className={section.id === 'vision' ? 'text-green-400' : ''}>
                          {section.icon}
                        </div>
                        {section.title}
                      </h2>
                      {section.id === 'vision' ? (
                        // AGC: Vision section with exact format provided (flashy green)
                        <div className="space-y-6 text-slate-300">
                          <div className="prose prose-invert max-w-none">
                            <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-l-4 border-green-400 p-4 rounded-lg mb-6">
                              <p className="text-green-300 font-semibold text-lg mb-2">🌟 FarmProHub – Vision and Purpose Document</p>
                            </div>

                            <h3 className="text-green-400 font-bold text-lg mt-6 mb-3">1. Introduction</h3>
                            <p className="text-slate-300 leading-relaxed mb-4">
                              FarmProHub is a long-term ecosystem designed to support the creation, governance, and growth of on-chain products built on Base. It represents a shift in how I approach innovation: from managing large teams and complex operational structures to building directly and rapidly with the support of modern AI tools.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-6">
                              The purpose of this document is to explain the origin of FarmProHub, the philosophy behind it, and how the community participates in and benefits from its evolution.
                            </p>

                            <h3 className="text-green-400 font-bold text-lg mt-6 mb-3">2. Background: A Transition, Not a Crisis</h3>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              For over two decades, I built and operated a technology company from the ground up that develops, manufactures, and sells software, firmware, and hardware products known as{' '}
                              <a
                                href="https://www.instagram.com/farmprobrasil/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#0072CE] hover:text-[#0088e6] underline inline-flex items-center gap-1"
                              >
                                precision agriculture
                                <ExternalLink className="w-3 h-3" />
                              </a>
                              .
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              We grew to 45 employees, shipped dozens of products, and expanded into multiple markets.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-4">
                              It required coordination across engineering, manufacturing, support, logistics, finance, HR, and compliance.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              Over time, the operational demands grew larger than the creative space I once had. I eventually stepped back from the CEO role not because the company was failing, but because the weight of managing everything left no room to create. I believed for a long time that this meant my creative phase was over.
                            </p>
                            <p className="text-[#0072CE] font-semibold mb-6">
                              Then AI changed everything.
                            </p>

                            <h3 className="text-green-400 font-bold text-lg mt-6 mb-3">3. Rediscovering Creation Through AI</h3>
                            <p className="text-slate-300 leading-relaxed mb-4">
                              AI allowed me to return to the core of what I always enjoyed: inventing, designing products, and building new systems from scratch.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-4">
                              Before AI, a single new product required managing engineers, programmers, designers, support teams, suppliers, and documentation processes. Every idea grew into a pipeline of meetings and operational tasks.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              With AI:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4 mb-4">
                              <li>I effectively became a one-person product studio</li>
                              <li>I could design, iterate, and prototype at the speed of thought</li>
                              <li>I no longer needed a full team to build software or blockchain systems</li>
                              <li>I regained the ability to create without operational overhead</li>
                            </ul>
                            <p className="text-slate-300 leading-relaxed mb-6">
                              FarmProHub emerged naturally from this shift.
                            </p>

                            <h3 className="text-green-400 font-bold text-lg mt-6 mb-3">4. What Is FarmProHub?</h3>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              FarmProHub is a governance and revenue-sharing ecosystem that supports every product I create on Base. It provides:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4 mb-6">
                              <li>A unified community instead of new companies for each idea</li>
                              <li>Shared incentives through 50% revenue distribution</li>
                              <li>Community-driven development through governance voting</li>
                              <li>A sustainable, long-term structure for on-chain product creation</li>
                            </ul>

                            <h3 className="text-green-400 font-bold text-lg mt-6 mb-3">5. Why Share 50% With the Community?</h3>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              <span className="font-semibold">1. I don't want to build a new corporate structure for every idea.</span>
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-4 ml-4">
                              FarmProHub replaces multiple companies with a single ecosystem where the community acts as the "growth arm."
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              <span className="font-semibold">2. Community alignment leads to better products.</span>
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-4 ml-4">
                              Users shape priorities, features, and direction.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-6 ml-4">
                              <span className="font-semibold">3. Fairness and transparency.</span>
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-6 ml-4">
                              If the community contributes to decision-making, it should share the upside.
                            </p>

                            <h3 className="text-green-400 font-bold text-lg mt-6 mb-3">6. Why Not Start a New Company?</h3>
                            <p className="text-slate-300 leading-relaxed mb-4">
                              After 25 years of entrepreneurship of solo founded/funded, I do not want to re-enter the cycle of building new teams, new legal structures, new operational systems, and large support infrastructures.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-4">
                              FarmProHub is intentionally lightweight — an ecosystem, not a corporation.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-6">
                              It gives me the freedom to create without bureaucracy, while giving the community the ability to adopt, promote, and expand the products organically.
                            </p>

                            <h3 className="text-green-400 font-bold text-lg mt-6 mb-3">7. Why Base?</h3>
                            <p className="text-slate-300 leading-relaxed mb-6">
                              Base offers the leadership, clarity, culture, and technical direction I was waiting for since 2016. Its orientation toward real use cases — not speculation — aligns perfectly with the long-term goals of FarmProHub.
                            </p>

                            <h3 className="text-green-400 font-bold text-lg mt-6 mb-3">8. Origami Battle – The First Product of FarmProHub</h3>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              Origami Battle demonstrates the philosophy behind FarmProHub:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4 mb-4">
                              <li>fully on-chain game logic</li>
                              <li>transparent and verifiable mechanics</li>
                              <li>NFT-based utility</li>
                              <li>strategy depth</li>
                              <li>on-chain actions instead of backend servers</li>
                              <li>human creativity + AI execution</li>
                            </ul>
                            <p className="text-slate-300 leading-relaxed mb-6">
                              It is not the end goal; it is the first brick in a larger ecosystem of tools, games, and on-chain applications.
                            </p>

                            <h3 className="text-green-400 font-bold text-lg mt-6 mb-3">9. Economic View and Personal Priorities</h3>
                            <p className="text-slate-300 leading-relaxed mb-4">
                              FarmProHub is not financially motivated.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-4">
                              I understand that most people in blockchain are driven primarily by economics, speculation, and upside. I respect that — and I will always try to build products that people genuinely want to use.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              But my personal objectives are different:
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              <span className="font-semibold">Priority #1: Be a great father to my son and daughter.</span>
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-4 ml-4">
                              This is the center of my life and the main reason I reorganized everything.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              <span className="font-semibold">Priority #2: Myself — recovering the years I neglected my own well-being to grow my company.</span>
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-4 ml-4">
                              This phase is about rebuilding balance after decades of being fully absorbed by operations.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              <span className="font-semibold">Priority #3: A shared commitment between my company (FarmPro) and FarmProHub.</span>
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-2 ml-4">
                              FarmProHub has no connection to my existing company.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-4 ml-4">
                              The only overlap is the name "FarmPro" because it was the nickname I used in 2016 when I created my{' '}
                              <a
                                href="https://www.reddit.com/r/ethereum/comments/58rfpa/how_does_blockchainethereum_will_adapt_in_farming/?share_id=ATF9X320sba9Y7pwtEvJt&utm_content=2&utm_medium=android_app&utm_name=androidcss&utm_source=share&utm_term=3"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#0072CE] hover:text-[#0088e6] underline inline-flex items-center gap-1"
                              >
                                Reddit account to discuss Ethereum
                                <ExternalLink className="w-3 h-3" />
                              </a>
                              {' '}— and I decided to keep it.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              I am not attempting to create wealth through FarmProHub.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              I am not operating in my peak "workaholic entrepreneur" mode anymore.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-6">
                              My goal is simply to create meaningful products at a sustainable pace, enabled by AI, supported by a community that finds value in using and growing them.
                            </p>

                            <h3 className="text-green-400 font-bold text-lg mt-6 mb-3">10. Final Note</h3>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              FarmProHub is not a hype vehicle or a speculative project.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              It is a structure designed around long-term alignment, transparent creation, and sustainable innovation.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              I am not building this for income.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-2">
                              I am not building this for attention.
                            </p>
                            <p className="text-slate-300 leading-relaxed mb-4">
                              I am building because creation is what I love — and AI finally made it possible for me to do it again.
                            </p>
                            <div className="bg-gradient-to-r from-green-500/30 to-emerald-500/30 border-2 border-green-400/50 p-4 rounded-lg mt-6">
                              <p className="text-green-200 leading-relaxed font-semibold">
                                FarmProHub is the bridge between my past as a traditional product builder and my future as a one-person AI-accelerated studio on Base.
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <ul className="space-y-3">
                          {section.content.map((item, index) => (
                            <li key={index} className="flex items-start gap-3 text-slate-300">
                              <span className="text-purple-400 mt-1">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </motion.div>
        )}

        {/* AGC: FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-4"
        >
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <HelpCircle className="w-6 h-6 text-blue-400" />
            Frequently Asked Questions
          </h2>
          
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden"
            >
              <button
                onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                className="w-full p-4 flex items-center justify-between text-left"
              >
                <span className="text-white font-medium">{faq.question}</span>
                {expandedFAQ === index ? (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                )}
              </button>
              {expandedFAQ === index && (
                <div className="px-4 pb-4 text-slate-400 text-sm border-t border-slate-700/50 pt-3">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </motion.div>

        {/* AGC: External Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50"
        >
          <h3 className="text-white font-semibold mb-4">External Resources</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <a
              href="https://docs.base.org"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors"
            >
              <ExternalLink className="w-4 h-4 text-blue-400" />
              <div>
                <p className="text-white text-sm font-medium">Base Documentation</p>
                <p className="text-slate-500 text-xs">Learn about Base blockchain</p>
              </div>
            </a>
            <a
              href="https://origamibattle.app"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors"
            >
              <ExternalLink className="w-4 h-4 text-purple-400" />
              <div>
                <p className="text-white text-sm font-medium">Origami Battles</p>
                <p className="text-slate-500 text-xs">First revenue-sharing project</p>
              </div>
            </a>
          </div>
        </motion.div>
      </div>
    </main>
  )
}

